/**
 * Free Losi Max Limit Notice
 *
 * Formats Free-plan Losi Max cooldown details for chat UI notices.
 *
 * Primary responsibilities:
 * - Normalize reset metadata from chat API payloads and annotations
 * - Format short cooldown text for fallback notices
 *
 * Dependencies:
 * - None
 */

export type FreeLosiMaxResetWindow = 'daily' | 'rolling'

export type FreeLosiMaxLimitDetails = {
  resetAt: string | null
  resetWindow: FreeLosiMaxResetWindow | null
  limit: number | null
  used: number | null
  windowHours: number | null
}

const readRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const readNullableString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value : null

const readNullableNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

export function readFreeLosiMaxLimitDetails(value: unknown): FreeLosiMaxLimitDetails | null {
  const payload = readRecord(value)
  const source = readRecord(payload?.subscription) ?? payload
  if (!source) return null

  const resetAt = readNullableString(source.resetAt)
  if (!resetAt) return null

  const resetWindow = source.resetWindow === 'daily' ? 'daily' : 'rolling'

  return {
    resetAt,
    resetWindow,
    limit: readNullableNumber(source.limit),
    used: readNullableNumber(source.used),
    windowHours: readNullableNumber(source.windowHours)
  }
}

export function formatFreeLosiMaxResetDuration(resetAt: string, now = new Date()) {
  const resetTime = new Date(resetAt).getTime()
  if (!Number.isFinite(resetTime)) return null

  const remainingMs = Math.max(0, resetTime - now.getTime())
  const totalMinutes = Math.max(1, Math.ceil(remainingMs / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h`
  return `${totalMinutes}m`
}

export function getFreeLosiMaxFallbackMessage(details: FreeLosiMaxLimitDetails | null) {
  const baseMessage = 'Free plan messages continue with Losi Mini.'
  if (!details?.resetAt) return baseMessage

  const duration = formatFreeLosiMaxResetDuration(details.resetAt)
  if (!duration) return baseMessage

  const resetText =
    details.resetWindow === 'daily'
      ? `Losi Max daily cap resets in ${duration}.`
      : `Losi Max cooldown clears in ${duration}.`

  return `${baseMessage} ${resetText}`
}
